import React, { useEffect, useState, useCallback } from 'react';
import api from '@/lib/api';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { PageHeader } from '@/components/Shared';
import { DangerConfirm } from '@/components/DangerConfirm';
import { money, shortDate } from '@/lib/format';
import { FileText, Undo2, RotateCcw, Users2, UserCog, CreditCard, Percent, ClipboardList, ShieldAlert, Trash2 } from 'lucide-react';
import { toast } from 'sonner';

/**
 * A single Danger Zone delete tool: pick a record, then confirm with password + CONFIRM.
 */
function DeleteTool({ tool }) {
  const [items, setItems] = useState([]);
  const [selected, setSelected] = useState('');
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [restoreInv, setRestoreInv] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(tool.listUrl);
      const rows = (tool.filter ? data.filter(tool.filter) : data);
      setItems(rows);
    } catch { setItems([]); }
  }, [tool]);
  useEffect(() => { load(); }, [load]);

  const chosen = items.find((r) => r.id === selected);

  const run = async (password) => {
    setBusy(true);
    try {
      const body = { password, confirm: 'CONFIRM' };
      if (tool.hasRestore) body.restore_inventory = restoreInv;
      await api.post(tool.deleteUrl(selected), body);
      toast.success(`${tool.title} — done`);
      setConfirmOpen(false); setSelected(''); setRestoreInv(false); load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
    finally { setBusy(false); }
  };

  const Icon = tool.icon;
  return (
    <Card className="p-5 border-[hsl(var(--destructive))]/25" data-testid={`tool-card-${tool.key}`}>
      <div className="flex items-center gap-2.5">
        <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-[hsl(var(--destructive))]/10 text-[hsl(var(--destructive))]"><Icon className="h-5 w-5" /></span>
        <div>
          <h3 className="font-heading font-semibold text-sm">{tool.title}</h3>
          <p className="text-xs text-muted-foreground">{items.length} record(s)</p>
        </div>
      </div>
      <div className="mt-4 space-y-2">
        <Select value={selected} onValueChange={setSelected}>
          <SelectTrigger data-testid={`tool-select-${tool.key}`}><SelectValue placeholder={`Select a ${tool.noun}…`} /></SelectTrigger>
          <SelectContent>
            {items.length === 0 && <div className="px-3 py-2 text-sm text-muted-foreground">No records</div>}
            {items.map((r) => <SelectItem key={r.id} value={r.id}>{tool.label(r)}</SelectItem>)}
          </SelectContent>
        </Select>
        {tool.hasRestore && selected && (
          <label className="flex items-center gap-2 text-xs cursor-pointer text-muted-foreground">
            <Checkbox checked={restoreInv} onCheckedChange={(v) => setRestoreInv(!!v)} data-testid={`tool-restore-${tool.key}`} /> Restore items to inventory
          </label>
        )}
        <Button variant="destructive" size="sm" className="w-full" disabled={!selected} onClick={() => setConfirmOpen(true)} data-testid={`tool-delete-${tool.key}`}>
          <Trash2 className="h-4 w-4 mr-1.5" /> {tool.title}
        </Button>
      </div>
      <DangerConfirm
        open={confirmOpen}
        onOpenChange={setConfirmOpen}
        title={tool.title}
        description={tool.warning}
        affectedText={chosen ? `Target: ${tool.label(chosen)}` : ''}
        onConfirm={run}
        busy={busy}
        confirmLabel={tool.title}
      />
    </Card>
  );
}

const TOOLS = [
  { key: 'invoice', title: 'Delete Invoice', noun: 'invoice', icon: FileText, hasRestore: true,
    listUrl: '/invoices', filter: (r) => r.type !== 'return',
    label: (r) => `${r.invoice_number} · ${r.customer_name} · ${money(r.total)}${r.voided ? ' (VOID)' : ''}`,
    deleteUrl: (id) => `/invoices/${id}/delete`,
    warning: 'Permanently deletes this invoice and its payments. Balances, reports and commissions update automatically. Cannot be undone.' },
  { key: 'return', title: 'Delete Return', noun: 'return', icon: Undo2, hasRestore: true,
    listUrl: '/invoices', filter: (r) => r.type === 'return',
    label: (r) => `${r.invoice_number} · ${r.customer_name} · ${money(Math.abs(r.total))}`,
    deleteUrl: (id) => `/invoices/${id}/delete`,
    warning: 'Permanently deletes this return credit note. The customer balance will update. Cannot be undone.' },
  { key: 'refund', title: 'Delete Refund', noun: 'refund', icon: RotateCcw,
    listUrl: '/payments?kind=refund',
    label: (r) => `${r.customer_name} · ${r.method} · ${money(Math.abs(r.amount))} · ${shortDate(r.created_at)}`,
    deleteUrl: (id) => `/payments/${id}/delete`,
    warning: 'Permanently deletes this refund record and recomputes the linked invoice. Cannot be undone.' },
  { key: 'payment', title: 'Delete Payment', noun: 'payment', icon: CreditCard,
    listUrl: '/payments?kind=payment',
    label: (r) => `${r.customer_name} · ${r.method} · ${money(r.amount)} · ${shortDate(r.created_at)}`,
    deleteUrl: (id) => `/payments/${id}/delete`,
    warning: 'Permanently deletes this payment and recomputes the linked invoice status/balance. Cannot be undone.' },
  { key: 'commission', title: 'Delete Commission', noun: 'commission record', icon: Percent,
    listUrl: '/invoices', filter: (r) => Math.abs(r.commission_amount || 0) > 0.005,
    label: (r) => `${r.invoice_number} · ${r.employee_name || '—'} · ${money(r.commission_amount)}`,
    deleteUrl: (id) => `/invoices/${id}/commission/delete`,
    warning: 'Sets the commission on this invoice to $0. The invoice itself is kept. Cannot be undone.' },
  { key: 'customer', title: 'Delete Customer', noun: 'customer', icon: Users2,
    listUrl: '/customers', filter: (r) => !r.is_default,
    label: (r) => `${r.name} · balance ${money(r.balance_due)}`,
    deleteUrl: (id) => `/customers/${id}/delete`,
    warning: 'Permanently deletes this customer with all their invoices and payments. Cannot be undone.' },
  { key: 'employee', title: 'Delete Employee', noun: 'employee', icon: UserCog,
    listUrl: '/users', filter: (r) => r.role === 'employee',
    label: (r) => `${r.name} · ${r.email}${r.deleted ? ' (archived)' : ''}`,
    deleteUrl: (id) => `/users/${id}/purge`,
    warning: 'PERMANENTLY deletes this employee account. Historical invoices keep the stored name. This is irreversible.' },
  { key: 'request', title: 'Delete Request', noun: 'request', icon: ClipboardList,
    listUrl: '/requests',
    label: (r) => `${(r.request_type || 'buy')} · ${r.product_name} · ${r.employee_name} · ${r.status}`,
    deleteUrl: (id) => `/requests/${id}/delete`,
    warning: 'Permanently deletes this request. Any invoice already generated from it is not affected. Cannot be undone.' },
];

export default function AdminTools() {
  return (
    <div>
      <PageHeader title="Admin Tools" subtitle="Danger Zone — destructive admin actions. Every action requires your password, typing CONFIRM, and is written to the Audit Log." testid="admin-tools-header" />
      <Card className="mb-6 border-[hsl(var(--destructive))]/40 bg-[hsl(var(--destructive))]/[0.04] p-4">
        <div className="flex items-center gap-2.5 text-[hsl(var(--destructive))]">
          <ShieldAlert className="h-5 w-5" />
          <p className="text-sm font-medium">These actions permanently remove data. Products and inventory are never deleted from this page (except optional stock restore on invoice/return deletes).</p>
        </div>
      </Card>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="admin-tools-grid">
        {TOOLS.map((t) => <DeleteTool key={t.key} tool={t} />)}
      </div>
    </div>
  );
}
